import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import {
	ICreatePayPlanResponse,
	IDeleteResponse,
	IIdInputPayPlan,
	IInfoInputPayPlan,
	IPayPlanInput,
	IPayPlanModelInputPayPlan,
	IUpdatesAvailablePayPlanResponse,
} from "@/PayPlans/types/api";
import { apiInstance, gql } from "@/services/httpAdapter";

// Commons Fragments
import {
	BonusDefinitionFragment,
	CallActivityPerformanceFragment,
	CommissionTiersFragment,
	CustomerSatisfactionStoreFragment,
	LongevityFragment,
	MetricFragment,
	InputValueFragment,
} from "@PayPlans/api/fragments/commons";

// Additional Fragments
import { GrossDepartmentalFragment, OperatingCostsFragment } from "@PayPlans/api/fragments/managers/departmental";
import { SFEBonusFragment } from "@PayPlans/api/fragments/managers/sales";

// Create services departmental pay plans
export async function createServiceDepartmentalPayPlan({ locationId, position }: IPayPlanInput) {
	const query = gql`
		mutation createServiceDepartmentalPayPlan($locationId: String!, $position: String!) {
			createServiceDepartmentalPayPlan(locationId: $locationId, position: $position) {
				name
				createdAt
				formatVersion
			}
		}
	`;

	const variables = { locationId, position };

	try {
		const response: GraphqlQueryResponse<"createServiceDepartmentalPayPlan", ICreatePayPlanResponse> = await apiInstance.post(
			"/graphql",
			{ query, variables }
		);
		return response.data.data?.createServiceDepartmentalPayPlan || null;
	} catch (error) {
		console.error(error);
		return null;
	}
}
// Update available services departmental pay plans
export async function updateServiceDepartmentalAvailablePayPlan({ locationId, information, position }: IInfoInputPayPlan) {
	const { _id, updatedBy, available } = information;
	const query = gql`
		mutation updateServiceDepartmentalAvailablePayPlan($locationId: String!, $input: UpdateAvailablePayPlanInput!, $position: String!) {
			updateServiceDepartmentalAvailablePayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				available
			}
		}
	`;

	const variables = {
		locationId,
		position,
		input: {
			_id,
			updatedBy,
			available,
		},
	};

	try {
		const response: GraphqlQueryResponse<"updateServiceDepartmentalAvailablePayPlan", IUpdatesAvailablePayPlanResponse> =
			await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateServiceDepartmentalAvailablePayPlan;
	} catch (err) {
		console.error(err);
		return null;
	}
}
// Add new services departmental pay plans
export async function cloneServiceDepartmentalPayPlan({ locationId, information, actualPosition }: IInfoInputPayPlan) {
	const { _id, createdBy, position } = information;
	const query = gql`
		mutation cloneServiceDepartmentalPayPlan($locationId: String!, $input: ClonePayPlan!, $position: String!) {
			cloneServiceDepartmentalPayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				bodyShop {
					...GrossDepartmental
				}
				parts {
					...GrossDepartmental
				}
				service {
					...GrossDepartmental
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				customerSatisfaction {
					...CustomerSatisfactionStore
				}
				ebe {
					...SFEBonus
				}
				pase {
					...SFEBonus
				}
				longevity {
					...Longevity
				}
				operatingCosts {
					...OperatingCosts
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${BonusDefinitionFragment}
		${GrossDepartmentalFragment}
		${CallActivityPerformanceFragment}
		${CommissionTiersFragment}
		${CustomerSatisfactionStoreFragment}
		${MetricFragment}
		${SFEBonusFragment}
		${LongevityFragment}
		${OperatingCostsFragment}
		${InputValueFragment}
	`;

	const variables = {
		locationId,
		input: {
			payPlanId: _id,
			createdBy,
			position,
		},
		position: actualPosition,
	};

	try {
		//Replace Object with PayPlan type
		const response: GraphqlQueryResponse<"cloneServiceDepartmentalPayPlan", Object> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.cloneServiceDepartmentalPayPlan || null;
	} catch (error) {
		console.error(error);
		return null;
	}
}
// Delete selected services departmental pay plans
export async function deleteServiceDepartmentalPayPlan({ locationId, _id, position }: IIdInputPayPlan) {
	const query = gql`
		mutation deleteServiceDepartmentalPayPlan($locationId: String!, $id: String!, $position: String!) {
			deleteServiceDepartmentalPayPlan(id: $id, locationId: $locationId, position: $position) {
				_id
				locationId
			}
		}
	`;

	const variables = {
		id: _id,
		locationId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"deleteServiceDepartmentalPayPlan", IDeleteResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.deleteServiceDepartmentalPayPlan || null;
	} catch (error) {
		console.error(error);
		return null;
	}
}
// Update selected services departmental pay plan
export async function updateServiceDepartmentalPayPlan({ locationId, payPlan, position }: IPayPlanModelInputPayPlan) {
	const query = gql`
		mutation updateServiceDepartmentalPayPlan(
			$locationId: String!
			$input: UpdateServiceDepartmentalPayPlanInput!
			$position: String!
		) {
			updateServiceDepartmentalPayPlan(input: $input, locationId: $locationId, position: $position) {
				_id
				locationId
				name
				position
				bodyShop {
					...GrossDepartmental
				}
				parts {
					...GrossDepartmental
				}
				service {
					...GrossDepartmental
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				customerSatisfaction {
					...CustomerSatisfactionStore
				}
				ebe {
					...SFEBonus
				}
				pase {
					...SFEBonus
				}
				longevity {
					...Longevity
				}
				operatingCosts {
					...OperatingCosts
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${BonusDefinitionFragment}
		${GrossDepartmentalFragment}
		${CallActivityPerformanceFragment}
		${CommissionTiersFragment}
		${CustomerSatisfactionStoreFragment}
		${MetricFragment}
		${SFEBonusFragment}
		${LongevityFragment}
		${OperatingCostsFragment}
		${InputValueFragment}
	`;

	const variables = {
		input: payPlan,
		locationId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"updateServiceDepartmentalPayPlan", Object> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.updateServiceDepartmentalPayPlan || null;
	} catch (err) {
		console.error(err);
		return null;
	}
}
// save new selected services departmental pay plan
export async function saveNewServiceDepartmentalPayPlan({ locationId, payPlan, position }: IPayPlanModelInputPayPlan) {
	const query = gql`
		mutation saveNewServiceDepartmentalPayPlan($locationId: String!, $input: SaveServiceDepartmentalPayPlan!, $position: String!) {
			saveNewServiceDepartmentalPayPlan(locationId: $locationId, input: $input, position: $position) {
				_id
				locationId
				name
				position
				bodyShop {
					...GrossDepartmental
				}
				parts {
					...GrossDepartmental
				}
				service {
					...GrossDepartmental
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				customerSatisfaction {
					...CustomerSatisfactionStore
				}
				ebe {
					...SFEBonus
				}
				pase {
					...SFEBonus
				}
				longevity {
					...Longevity
				}
				operatingCosts {
					...OperatingCosts
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${BonusDefinitionFragment}
		${GrossDepartmentalFragment}
		${CallActivityPerformanceFragment}
		${CommissionTiersFragment}
		${CustomerSatisfactionStoreFragment}
		${MetricFragment}
		${SFEBonusFragment}
		${LongevityFragment}
		${OperatingCostsFragment}
		${InputValueFragment}
	`;

	const variables = {
		locationId,
		input: payPlan,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"saveNewServiceDepartmentalPayPlan", Object> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.saveNewServiceDepartmentalPayPlan;
	} catch (err) {
		console.error(err);
		return null;
	}
}
